import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  calendarEventSchema,
  type CalendarEventFormValues,
} from '@/components/Calendars/calendarSchemas'
import { useUpdateCalendarEvent } from '@/hooks/useCalendarEvents'
import type { CalendarEventRead } from '@/types'

interface EditEventDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  calendarId: string
  event: CalendarEventRead
}

export function EditEventDialog({
  open,
  onOpenChange,
  calendarId,
  event,
}: EditEventDialogProps) {
  const updateEvent = useUpdateCalendarEvent(calendarId)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CalendarEventFormValues>({
    resolver: zodResolver(calendarEventSchema),
    defaultValues: {
      name: event.name,
      description: event.description ?? '',
    },
  })

  useEffect(() => {
    if (open) {
      reset({
        name: event.name,
        description: event.description ?? '',
      })
    }
  }, [open, event, reset])

  const onSubmit = (values: CalendarEventFormValues) => {
    updateEvent.mutate(
      {
        eventId: event.id,
        payload: {
          name: values.name,
          description: values.description || null,
        },
      },
      {
        onSuccess: () => {
          onOpenChange(false)
        },
      }
    )
  }

  return (
    <Dialog
      open={open}
      onOpenChange={updateEvent.isPending ? undefined : onOpenChange}
    >
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Event</DialogTitle>
          <DialogDescription>
            Update the event name and description.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-event-name">Name</Label>
            <Input
              id="edit-event-name"
              {...register('name')}
              placeholder="Event name"
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-event-desc">Description</Label>
            <Textarea
              id="edit-event-desc"
              {...register('description')}
              placeholder="Optional description"
              rows={3}
            />
            {errors.description && (
              <p className="text-sm text-destructive">
                {errors.description.message}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateEvent.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updateEvent.isPending}
              className="cursor-pointer"
            >
              {updateEvent.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
